import { prompt, loaderPrompt } from './prompts';
import Swal from 'sweetalert2';
import { sendPostRequest } from './utils';
import { addAlert, setRedirectAlert } from './alerts';

// Constants for configuration and status checks to avoid magic strings.
const POLLING_INTERVAL_MS = 5000;
const API_STATUS = {
    LOADING: 'loading',
    REDIRECT: 'redirect',
    MANUAL: 'manual',
    NO_REQUEST: 'no_request',
};
const HTTP_STATUS = {
    NOT_FOUND: 404,
};
const modalClass = {
    container: 'scanner-modal',
    htmlContainer: 'scanner-container',
    cancelButton: 'btn btn-secondary',
};

const modalWidth = '75vw';

/**
 * Manages scanner data polling and related user interactions.
 * This class handles two polling modes:
 * 1. Passive background polling to detect new scans initiated by an external device.
 * 2. Active polling after a user submits a code manually via a modal.
 */
class ScannerHandler {
    /**
     * Initializes the handler, gets a reference to the scanner button,
     * and sets up initial state.
     */
    constructor() {
        this.backgroundPollIntervalId = null;
        this.activePollIntervalId = null;
        this.isPolling = false;
        this.scannerButton = document.getElementById('scannerBtn');

        if (this.scannerButton) {
            this.init();
        }
    }

    /**
     * Binds event listeners and starts the initial background polling.
     */
    init() {
        this.scannerButton.addEventListener('click', () =>
            this.openManualInputModal(),
        );
        this.bindKeyboardShortcut();
        this.startBackgroundPolling();
    }

    /**
     * Binds the F2 keyboard shortcut to open the manual input modal,
     * mirroring a click on the scanner button. Ignored while another dialog
     * is already open.
     */
    bindKeyboardShortcut() {
        document.addEventListener('keydown', (event) => {
            if (event.key !== 'F2' || Swal.isVisible()) {
                return;
            }

            event.preventDefault();
            this.openManualInputModal();
        });
    }

    // --- POLLING MANAGEMENT ---

    /**
     * Starts the continuous, passive background check for new scans.
     * Does nothing if a poll is already active.
     */
    startBackgroundPolling() {
        if (this.isPolling) return;

        this.isPolling = true;
        this.backgroundPollIntervalId = setInterval(
            () => this.checkCollectorDataInBackground(),
            POLLING_INTERVAL_MS,
        );
    }

    /**
     * Stops the passive background polling.
     */
    stopBackgroundPolling() {
        clearInterval(this.backgroundPollIntervalId);
        this.backgroundPollIntervalId = null;
        this.isPolling = false;
    }

    /**
     * Polls for results after a manual code submission. This poll is "active"
     * and shows a loader to the user.
     */
    pollForResultAfterSubmit() {
        // Ensure no other active poll is running.
        clearInterval(this.activePollIntervalId);

        this.activePollIntervalId = setInterval(async () => {
            try {
                const response = await fetch('/api/collectors/check/');
                const data = await response.json();

                // If the process is still loading, just wait for the next interval.
                const isStillLoading =
                    response.ok &&
                    [API_STATUS.LOADING, API_STATUS.NO_REQUEST].includes(
                        data.status,
                    );
                if (isStillLoading) {
                    return;
                }

                // For any definitive result (success or error), stop polling and close the loader.
                clearInterval(this.activePollIntervalId);
                this.activePollIntervalId = null;
                Swal.close();

                if (response.ok) {
                    await this.handleApiResponse(data);
                } else {
                    // Handle specific client/server errors from the API.
                    const errorMessage =
                        data.message ||
                        (response.status >= 500
                            ? gettext(
                                  'Wystąpił błąd serwera. Spróbuj ponownie później.',
                              )
                            : gettext(
                                  'Nie znaleziono urządzenia albo podzespołu.',
                              ));
                    addAlert('error', errorMessage);
                    this.startBackgroundPolling();
                }
            } catch (error) {
                // Handle network errors.
                console.error('Error during active polling:', error);
                clearInterval(this.activePollIntervalId);
                this.activePollIntervalId = null;
                Swal.close();
                addAlert(
                    'error',
                    gettext(
                        'Błąd połączenia. Sprawdź sieć i spróbuj ponownie.',
                    ),
                );
                this.startBackgroundPolling();
            }
        }, POLLING_INTERVAL_MS);
    }

    // --- API & DATA HANDLING ---

    /**
     * Fetches collector status in the background. Only acts if no user-facing modal is open.
     */
    async checkCollectorDataInBackground() {
        if (Swal.isVisible()) {
            return; // Don't interrupt an active user dialog.
        }

        try {
            const response = await fetch('/api/collectors/check/');

            // Ignore the "no request" status (404), which is normal between scans.
            if (response.status === HTTP_STATUS.NOT_FOUND) {
                return;
            }

            const data = await response.json();
            const isFinished =
                response.ok &&
                data.status &&
                data.status !== API_STATUS.LOADING;

            // A successful, finished result or a specific API error requires user interaction.
            if (isFinished || !response.ok) {
                this.stopBackgroundPolling();
                if (response.ok) {
                    await this.handleApiResponse(data);
                } else {
                    addAlert(
                        'error',
                        data.message || gettext('Wystąpił nieoczekiwany błąd.'),
                    );
                }
            }
        } catch (error) {
            console.error('Error in background scanner polling:', error);
            this.stopBackgroundPolling(); // Stop polling on network or parsing errors.
        }
    }

    /**
     * Routes the API response to the appropriate UI handler and then ensures
     * background polling is resumed.
     * @param {object} resultData - The data from the /api/collectors/check/ endpoint.
     */
    async handleApiResponse(resultData) {
        try {
            if (resultData.status === API_STATUS.REDIRECT) {
                await this.promptUserForRedirect(resultData);
            } else if (resultData.status === API_STATUS.MANUAL) {
                await this.showManualSelection(resultData);
            } else {
                addAlert(
                    'error',
                    resultData.message ||
                        gettext('Wystąpił nieoczekiwany błąd.'),
                );
            }
        } finally {
            // Ensure background polling resumes if it was stopped.
            if (!this.isPolling) {
                this.startBackgroundPolling();
            }
        }
    }

    /**
     * Submits the manually entered code and initiates active polling for the result.
     * @param {string} code - The product code entered by the user.
     */
    async submitManualCode(code) {
        try {
            await sendPostRequest('/api/collectors/collect/manual/', {
                product: code,
            });
            loaderPrompt(gettext('Przetwarzanie...'), modalWidth, modalClass);
            this.pollForResultAfterSubmit();
        } catch (error) {
            const message =
                error.response?.data?.message ||
                gettext('Błąd podczas wysyłania ręcznego kodu.');
            console.error('Error sending manual input:', error);
            Swal.close();
            addAlert('error', message);
            this.startBackgroundPolling();
        }
    }

    // --- UI & MODAL MANAGEMENT ---

    /**
     * Opens a modal for manual product code input.
     * Stops background polling to prevent conflicts.
     */
    async openManualInputModal() {
        this.stopBackgroundPolling();

        const { isConfirmed, value } = await prompt({
            title: gettext('Wpisz kod urządzenia lub podzespołu ręcznie'),
            html: `<input type="text" id="manualCodeInput" class="swal2-input code-input" placeholder="${gettext('Kod urządzenia lub podzespołu')}" style="text-transform: uppercase" autofocus>`,
            confirmText: gettext('Wyślij'),
            showCancelButton: true,
            cancelText: gettext('Anuluj'),
            customClass: modalClass,
            width: modalWidth,
            preConfirm: () => {
                const input = document.getElementById('manualCodeInput');
                if (!input || !input.value) {
                    Swal.showValidationMessage(
                        gettext('Pole nie może być puste'),
                    );
                    return false;
                }
                return input.value.replace(/\s/g, '').toUpperCase();
            },
        });

        if (isConfirmed && value) {
            await this.submitManualCode(value);
        } else {
            this.startBackgroundPolling(); // Resume if the user cancelled.
        }
    }

    /**
     * Displays a confirmation prompt before redirecting the user.
     * @param {object} resultData - The API response containing the redirect URL and message.
     */
    async promptUserForRedirect(resultData) {
        const messageForHtml = resultData.message.replace(/\n/g, '<br>');
        await prompt({
            title: gettext('Wykryto akcję'),
            html: `<div class="text-center">${messageForHtml}</div>`,
            confirmText: gettext('Kontynuuj'),
            cancelText: gettext('Anuluj'),
            customClass: modalClass,
            width: modalWidth,
            confirmAction: () => {
                setRedirectAlert('info', resultData.message);
                window.location.href = resultData.url;
            },
        });
    }

    /**
     * Displays a modal with a list of manuals for the user to choose from.
     * @param {object} resultData - The API response containing the list of manuals.
     */
    async showManualSelection(resultData) {
        const manualsHtml = this.generateManualSelectionHtml(
            resultData.manuals,
            resultData.message,
        );

        await prompt({
            title: '',
            html: manualsHtml,
            showCancelButton: true,
            cancelText: gettext('Zamknij'),
            showConfirmButton: false,
            customClass: modalClass,
            width: modalWidth,
            didOpen: () => {
                this.attachManualItemClickListeners(resultData);
            },
        });
    }

    /**
     * Generates the HTML string for the manual selection list.
     * @param {Array<object>} manuals - List of manual objects.
     * @param {string} messageText - A message to display above the list.
     * @returns {string} The generated HTML.
     */
    generateManualSelectionHtml(manuals, messageText) {
        let itemsHtml = manuals
            .map((manual) => {
                const matchPercent = manual.match_strength
                    ? `${manual.match_strength * 25}%`
                    : 'UNI';
                return `
                <button class="manual-selection-item strength-${manual.match_strength}"
                        data-manual-id="${manual.id}"
                        data-label="${manual.label}">
                    <strong>${matchPercent}</strong>
                    <span>${gettext('Instrukcja')} #${manual.id}</span>
                    <span>${manual.station_type_display}</span>
                    <strong>${manual.device_group || manual.component_group || '-'}</strong>
                    <span>${manual.device_type || manual.component_group || '-'}</span>
                    <strong>${manual.device_manufacturer || '-'}</strong>
                </button>
            `;
            })
            .join('');

        return `
            <div class="title">${messageText}</div>
            <div class="subtitle margin-top-2 mmargin-bottom-2">${gettext('Wybierz instrukcję do użycia:')}</div>
            <div class="manual-selection-list">
                ${itemsHtml || `<p>${gettext('Nie znaleziono pasujących instrukcji.')}</p>`}
            </div>
        `;
    }

    /**
     * Attaches click event listeners to each manual item in the selection modal.
     * Clicking an item leads to a final confirmation before starting the process.
     * @param {object} resultData - The original API response data (used for enova_id).
     */
    attachManualItemClickListeners(resultData) {
        document
            .querySelectorAll('.scanner-modal .manual-selection-item')
            .forEach((item) => {
                item.addEventListener('click', () => {
                    const selectedManualId = item.dataset.manualId;
                    const label = item.dataset.label;
                    const enovaId = resultData.enova_id;

                    Swal.close();

                    prompt({
                        title: gettext(
                            'Czy na pewno rozpocząć proces z wybraną instrukcją?',
                        ),
                        text: label,
                        customClass: modalClass,
                        width: modalWidth,
                        confirmAction: () => {
                            window.location.href = `/executions/start/${selectedManualId}/${enovaId}/`;
                        },
                        cancelAction: () => {
                            // Re-open the selection modal if the user cancels the confirmation.
                            this.showManualSelection(resultData);
                        },
                    });
                });
            });
    }
}

// --- INITIALIZATION ---

/**
 * Ensures the ScannerHandler is initialized once the page bundle is loaded.
 */
document.addEventListener('bundleLoaded', () => {
    if (!window.scannerHandler) {
        window.scannerHandler = new ScannerHandler();
    }
});
